import TableContext from '../index'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import useScreen from 'utils/screen'
import { breakpoint } from 'utils/consts/breakpoints'
import randomString from 'random-string'
import { HEADER_CLASSNAME, TRANSITION_DURATION } from '../../CollapsibleHeader'
import type { FiltersCollection } from 'utils/filtersCollections/filtersCollectionsRegistry'

type Container = keyof typeof breakpoint
type ContainersMap = Container[]
type ElementToHide = HTMLElement & {
  __initialHeight: number
  __containerOnly: ContainersMap
  __uniqueId: string
}

export type RefCallbackOptions = {
  containerOnly?: ContainersMap
}

const SCROLL_GAP = 50
const OBSERVER_DELAY = 1000
const UNIQUE_ID_LENGTH = 10

let timeoutId: ReturnType<typeof setTimeout>
let lastScrollTop = 0
let isHeadersVisible = true
let isAnimated = false

/**
 * Обработчик мутаций DOM для обновления высоты заголовков
 */
const mutationHandler = (mutations: MutationRecord[]) => {
  mutations.forEach(mutation => {
    const header: ElementToHide | null = (
      mutation.target as ElementToHide
    ).closest(`.${HEADER_CLASSNAME}`)
    if (!header) return

    header.style.maxHeight = `${header.scrollHeight}px`
    header.__initialHeight = header.scrollHeight
    header.addEventListener('transitionend', transitionEndHandler, {
      once: true,
    })
  })
}

const mutationObserver = new MutationObserver(mutationHandler)

/**
 * Запускает наблюдение за элементами с задержкой
 */
const startObserve = (elements: ElementToHide[]) => {
  timeoutId = setTimeout(() => {
    elements.forEach(node => {
      mutationObserver.observe(node, { childList: true, subtree: true })
    })
  }, OBSERVER_DELAY)
}

/**
 * Обработчик окончания анимации перехода
 */
const transitionEndHandler = (e: TransitionEvent) => {
  const target = (
    (e.target as HTMLElement).classList.contains(HEADER_CLASSNAME)
      ? e.target
      : (e.target as HTMLElement).closest(`.${HEADER_CLASSNAME}`)
  ) as HTMLElement
  if (target) {
    target.style.overflow = 'visible'
  }
}

/**
 * Обработчик отложенного раскрытия элемента
 */
const batchedRevealElement = (e: TransitionEvent) => {
  const target = e.target as ElementToHide
  if (target) {
    target.style.maxHeight = `${target.__initialHeight}px`
    target.addEventListener('transitionend', transitionEndHandler, {
      once: true,
    })
  }
}

/**
 * Проверяет, достигнут ли конец прокрутки
 */
const isScrolledToBottom = (element: HTMLElement): boolean => {
  return element.scrollHeight - element.scrollTop === element.clientHeight
}

/**
 * Обновляет стили элементов при прокрутке вниз
 */
const updateElementsOnScrollDown = (
  elements: ElementToHide[],
  scrollTop: number,
  heightToStartAnimation: number
) => {
  // Условие сработает в двух случаях:
  // 1. Когда мы не прокрутили достаточно, чтобы начать анимацию (heightToStartAnimation > scrollTop)
  // 2. Когда заголовки уже скрыты (!isHeadersVisible)
  if (heightToStartAnimation > scrollTop || !isHeadersVisible) {
    return
  }

  isHeadersVisible = false
  isAnimated = true

  elements.forEach(node => {
    const newHeight = Math.max(node.__initialHeight - scrollTop, 0)
    node.style.maxHeight = `${newHeight}px`
    node.style.overflow = 'hidden'
    node.removeEventListener('transitionend', transitionEndHandler)
  })

  setTimeout(() => {
    isAnimated = false
  }, TRANSITION_DURATION)
}

/**
 * Обновляет стили элементов при прокрутке вверх
 */
const updateElementsOnScrollUp = (
  elements: ElementToHide[],
  scrollTop: number,
  heightToStartAnimation: number,
  isScrolledToBottom: boolean
) => {
  // Сработает в случае:
  // Когда прокрутили вниз, заголовки скрываются (идёт анимаци), крутим на самый верх, и чтоб заголовки снова стали видимыми
  // добавляем обработчик transitionend который вновь их раскроет
  if (heightToStartAnimation > scrollTop && isAnimated && !isScrolledToBottom) {
    elements.forEach(node => {
      node.addEventListener('transitionend', batchedRevealElement, {
        once: true,
      })
    })
  }

  if (heightToStartAnimation < scrollTop || isHeadersVisible || isAnimated) {
    return
  }

  isHeadersVisible = true
  elements.forEach(node => {
    node.style.maxHeight = `${node.__initialHeight}px`
    node.addEventListener('transitionend', transitionEndHandler, {
      once: true,
    })
  })
}

const TableContextProvider = ({ children }) => {
  const [reactTableProps, setReactTableProps] = useState()
  const [tableContentRef, _setTableContentRef] = useState<HTMLElement | null>(
    null
  )
  const [elementsToHide, _setCollapsibleHeader] = useState<ElementToHide[]>([])
  const [filtersCollection, setFiltersCollection] =
    useState<FiltersCollection | null>(null)
  const { currentContainer } = useScreen() as {
    currentContainer: Container | undefined
  }

  const setTableContentRef = useCallback(node => {
    if (node !== null) {
      _setTableContentRef(node)
    }
  }, [])

  const setCollapsibleHeader = useCallback(
    (_node: HTMLElement | null, options: RefCallbackOptions = {}) => {
      const node = _node as ElementToHide
      if (node === null) return

      if (
        node.__uniqueId &&
        elementsToHide.some(el => el.__uniqueId === node.__uniqueId)
      ) {
        return
      }

      if (options.containerOnly) {
        node.__containerOnly = options.containerOnly
      }
      node.__uniqueId = randomString({ length: UNIQUE_ID_LENGTH })

      _setCollapsibleHeader(prev => [...prev, node as ElementToHide])
    },
    [elementsToHide]
  )

  const handleTableScroll = useCallback(
    (e: Event) => {
      if (!tableContentRef || !currentContainer) return

      const scrollTop = (e.currentTarget as HTMLElement).scrollTop
      const elements = elementsToHide.filter(
        node =>
          !node.__containerOnly ||
          node.__containerOnly.includes(currentContainer)
      )
      const heightToStartAnimation = elements.reduce(
        (acc, el) => acc + el.__initialHeight,
        SCROLL_GAP
      )
      const isScrollDown = scrollTop > lastScrollTop
      const isBottom = isScrolledToBottom(tableContentRef)

      mutationObserver.disconnect()
      clearTimeout(timeoutId)

      if (isScrollDown) {
        elements.forEach(node => {
          node.removeEventListener('transitionend', batchedRevealElement)
        })

        updateElementsOnScrollDown(elements, scrollTop, heightToStartAnimation)
      } else {
        updateElementsOnScrollUp(
          elements,
          scrollTop,
          heightToStartAnimation,
          isBottom
        )
      }

      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop
      startObserve(elementsToHide)
    },
    [tableContentRef, elementsToHide, currentContainer]
  )

  // Инициализация высоты элементов при изменении контейнера
  useLayoutEffect(() => {
    elementsToHide.forEach(node => {
      node.style.maxHeight = 'none'
      node.__initialHeight = node.offsetHeight
    })
  }, [currentContainer])

  // Установка обработчика прокрутки
  useEffect(() => {
    if (tableContentRef) {
      tableContentRef.onscroll = handleTableScroll
    }
  }, [tableContentRef, handleTableScroll])

  // Инициализация наблюдателя за элементами
  useLayoutEffect(() => {
    elementsToHide.forEach(node => {
      node.__initialHeight = node.offsetHeight
      node.style.maxHeight = `${node.offsetHeight}px`
      mutationObserver.observe(node, { childList: true, subtree: true })
    })
  }, [elementsToHide])

  const value = useMemo(
    () => ({
      reactTableProps,
      setReactTableProps,
      setTableContentRef,
      tableContentRef,
      setCollapsibleHeader,
      filtersCollection,
      setFiltersCollection,
    }),
    [
      reactTableProps,
      setTableContentRef,
      setReactTableProps,
      filtersCollection,
    ]
  )

  return <TableContext.Provider value={value}>{children}</TableContext.Provider>
}

export default TableContextProvider
